import React from "react";
import { useTranslation } from "react-i18next";
import { LivePal } from "../../types/Pal";
import GearHeadings from "../../ui/gearheadings";
import GearButton from "../../ui/gearbutton";
import {
  IoLocationOutline,
  IoMusicalNotes,
  IoTrashOutline,
} from "react-icons/io5";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteLivepal } from "../../backend/api/livepal";
import { toast } from "react-hot-toast";

interface UserVenuesProps {
  livepalsData: LivePal[];
  onEdit?: (pal: LivePal) => void;
}

export default function UserVenues({ livepalsData, onEdit }: UserVenuesProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate: deleteVenueMutation } = useMutation({
    mutationFn: (id: number) => deleteLivepal(id, i18n.language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-livepals"] });
      toast.success(t("pal.deleted", "Locale eliminato con successo"));
    },
    onError: () => {
      toast.error(t("pal.deleteFailed", "Errore nell'eliminazione"));
    },
  });

  if (!livepalsData || livepalsData.length === 0) {
    return (
      <div className="mt-8 profile-card w-full pt-6">
        <GearHeadings variant="h3" className="text-secondary mb-6">
          {t("myPals.venues", "I Miei Locali")}
        </GearHeadings>
        <p className="text-white/40 text-center py-8 italic">
          {t("myPals.noVenues", "Nessun locale aggiunto.")}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-8 profile-card w-full pt-6">
      <GearHeadings variant="h3" className="text-secondary mb-6">
        {t("myPals.venues", "I Miei Locali")}
      </GearHeadings>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full">
        {livepalsData.map((venue) => (
          <VenueCard
            key={venue.id}
            venue={venue}
            onDelete={() => venue.id && deleteVenueMutation(venue.id)}
            onEdit={() => onEdit?.(venue)}
          />
        ))}
      </div>
    </div>
  );
}

function VenueCard({
  venue,
  onDelete,
  onEdit,
}: {
  venue: LivePal;
  onDelete: () => void;
  onEdit: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="bg-[#2a1b41] border border-white/10 rounded-[28px] overflow-hidden flex flex-col hover:border-white/20 transition-all group p-5">
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-2xl bg-primary/20 text-primary">
            <IoMusicalNotes size={24} />
          </div>
          <div>
            <h4 className="text-white font-bold text-lg leading-tight line-clamp-1">
              {venue.name}
            </h4>
            <p className="text-white/40 text-sm">
              {venue.venueType === "acoustic"
                ? t("filters.acoustic")
                : t("filters.fullBand")}
            </p>
          </div>
        </div>
        <button
          onClick={onDelete}
          className="p-2 text-white/20 hover:text-red-500 transition-colors"
          title={t("common.delete")}
        >
          <IoTrashOutline size={20} />
        </button>
      </div>

      <div className="flex items-center gap-2 text-white/60 text-sm mb-6">
        <IoLocationOutline className="text-secondary" />
        <span>
          {venue.city}, {venue.region}
        </span>
      </div>

      <div className="flex gap-3 mt-auto">
        <GearButton
          variant="secondary"
          onClick={onEdit}
          className="flex-1 !rounded-full font-bold shadow-lg shadow-secondary/20 hover:shadow-secondary/30 transition-all active:scale-95"
        >
          {t("common.edit", "Edit")}
        </GearButton>
        <GearButton
          variant="primary"
          className="flex-1 !rounded-full font-bold bg-[#5A18C4] !border-none shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all active:scale-95 text-xs"
        >
          {t("forms.equipmentShort", "Attrezzatura")} (
          {venue.systems?.length || 0})
        </GearButton>
      </div>
    </div>
  );
}
